import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { joinWaitlist } from "../lib/api";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

function FormShell({ role, children, onSubmit, loading, submitted, label }) {
    if (submitted) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border border-[#10B981]/30 bg-emerald-50 p-8 text-center"
                data-testid={`waitlist-success-${role}`}
            >
                <div className="mx-auto w-14 h-14 rounded-full bg-[#10B981] grid place-items-center mb-4 shadow-[0_8px_24px_rgba(16,185,129,0.35)]">
                    <Check className="w-7 h-7 text-white" strokeWidth={3} />
                </div>
                <h4 className="text-xl font-bold text-[#070F1D] mb-1">
                    You're on the list.
                </h4>
                <p className="text-slate-600 text-sm">
                    We'll email you the moment MechXpress goes live in your
                    area.
                </p>
            </motion.div>
        );
    }
    return (
        <form
            onSubmit={onSubmit}
            className="space-y-3"
            data-testid={`waitlist-form-${role}`}
        >
            {children}
            <Button
                type="submit"
                disabled={loading}
                data-testid={`waitlist-submit-${role}`}
                className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-full h-12 shadow-[0_12px_24px_-8px_rgba(16,185,129,0.5)] hover:shadow-[0_16px_32px_-8px_rgba(16,185,129,0.6)] transition-all group"
            >
                {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <span className="inline-flex items-center gap-2">
                        {label}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                )}
            </Button>
            <p className="text-xs text-slate-500 text-center pt-1">
                No spam. One email when we launch near you.
            </p>
        </form>
    );
}

export default function WaitlistForm({ defaultTab = "customer" }) {
    const [tab, setTab] = useState(defaultTab);

    const [cEmail, setCEmail] = useState("");
    const [cZip, setCZip] = useState("");
    const [cLoading, setCLoading] = useState(false);
    const [cDone, setCDone] = useState(false);

    const [mEmail, setMEmail] = useState("");
    const [mName, setMName] = useState("");
    const [mSpecialty, setMSpecialty] = useState("");
    const [mLoading, setMLoading] = useState(false);
    const [mDone, setMDone] = useState(false);

    const submit = async (role) => {
        try {
            if (role === "customer") {
                setCLoading(true);
                await joinWaitlist({
                    email: cEmail,
                    role: "customer",
                    zip_code: cZip || null,
                });
                setCDone(true);
                toast.success("You're on the waitlist!");
            } else {
                setMLoading(true);
                await joinWaitlist({
                    email: mEmail,
                    role: "mechanic",
                    name: mName || null,
                    specialty: mSpecialty || null,
                });
                setMDone(true);
                toast.success("Welcome to the network — we'll be in touch.");
            }
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Something went wrong. Try again.";
            toast.error(msg);
        } finally {
            setCLoading(false);
            setMLoading(false);
        }
    };

    const inputCls =
        "h-12 bg-white border-slate-200 text-[#070F1D] placeholder:text-slate-400 rounded-xl focus-visible:ring-[#10B981] focus-visible:ring-offset-0 focus-visible:border-[#10B981]";

    return (
        <div
            data-testid="waitlist-wrapper"
            className="relative rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-[0_20px_60px_-20px_rgba(7,15,29,0.15)]"
        >
            <Tabs value={tab} onValueChange={setTab} className="relative">
                <TabsList
                    data-testid="waitlist-tabs"
                    className="w-full grid grid-cols-2 bg-slate-100 rounded-full p-1 h-12"
                >
                    <TabsTrigger
                        value="customer"
                        data-testid="tab-customer"
                        className="rounded-full text-sm font-semibold data-[state=active]:bg-[#070F1D] data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 transition-all"
                    >
                        I'm a Customer
                    </TabsTrigger>
                    <TabsTrigger
                        value="mechanic"
                        data-testid="tab-mechanic"
                        className="rounded-full text-sm font-semibold data-[state=active]:bg-[#10B981] data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 transition-all"
                    >
                        I'm a Mechanic
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="customer" className="mt-6">
                    <FormShell
                        role="customer"
                        loading={cLoading}
                        submitted={cDone}
                        label="Get Early Access"
                        onSubmit={(e) => {
                            e.preventDefault();
                            submit("customer");
                        }}
                    >
                        <Input
                            type="email"
                            required
                            value={cEmail}
                            onChange={(e) => setCEmail(e.target.value)}
                            placeholder="you@email.com"
                            data-testid="customer-email-input"
                            className={inputCls}
                        />
                        <Input
                            value={cZip}
                            onChange={(e) => setCZip(e.target.value)}
                            placeholder="ZIP code (optional)"
                            data-testid="customer-zip-input"
                            className={inputCls}
                        />
                    </FormShell>
                </TabsContent>

                <TabsContent value="mechanic" className="mt-6">
                    <FormShell
                        role="mechanic"
                        loading={mLoading}
                        submitted={mDone}
                        label="Join as a Mechanic"
                        onSubmit={(e) => {
                            e.preventDefault();
                            submit("mechanic");
                        }}
                    >
                        <Input
                            type="email"
                            required
                            value={mEmail}
                            onChange={(e) => setMEmail(e.target.value)}
                            placeholder="you@shop.com"
                            data-testid="mechanic-email-input"
                            className={inputCls}
                        />
                        <Input
                            value={mName}
                            onChange={(e) => setMName(e.target.value)}
                            placeholder="Your name or shop (optional)"
                            data-testid="mechanic-name-input"
                            className={inputCls}
                        />
                        <Input
                            value={mSpecialty}
                            onChange={(e) => setMSpecialty(e.target.value)}
                            placeholder="Specialty (e.g., brakes, diagnostics)"
                            data-testid="mechanic-specialty-input"
                            className={inputCls}
                        />
                    </FormShell>
                </TabsContent>
            </Tabs>
        </div>
    );
}
